/**
 * ThinkingPanel — V3 migrated.
 *
 * Replaces the bespoke disclosure shell with <OrkaDisclosure> + agent-tone
 * grammar (`thinking` / `running` / `done` / `failure`). Step-list interior
 * preserved verbatim — only raw text-slate-* / text-emerald-* / text-amber-*
 * colours swapped for design-system tokens.
 *
 * Two modes (unchanged behaviour):
 *   isLive=true   → polls /api/ai/chat/live/{liveRunId} for real tool steps
 *   scratchpad=[] → renders the FINAL trail returned by the backend
 *
 * Test IDs preserved: thinking-panel, thinking-panel-toggle, thinking-panel-body,
 * thinking-step-${step}.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Loader2, CheckCircle2, AlertTriangle, Clock, Brain,
  Search, Wrench, ListChecks,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { OrkaDisclosure } from './orka';

// Friendly per-domain labels so the panel reads like English to non-techs.
const DOMAIN_LABEL = {
  'orka.tasks':       { label: 'Orka tasks',         Icon: ListChecks },
  'orka.calendar':    { label: 'Calendar',           Icon: Clock },
  'orka.eod':         { label: 'EOD summaries',      Icon: ListChecks },
  'orka.org':         { label: 'Organisation',       Icon: ListChecks },
  'orka.gmail':       { label: 'Gmail',              Icon: Search },
  'orka.gdrive':      { label: 'Google Drive',       Icon: Search },
  'orka.gdocs':       { label: 'Google Docs',        Icon: Search },
  'orka.gsheets':     { label: 'Google Sheets',      Icon: Search },
  'orka.gslides':     { label: 'Google Slides',      Icon: Search },
  'orka.atlassian':   { label: 'Jira / Confluence',  Icon: Search },
  'orka.retrieval':   { label: 'Multi-source search', Icon: Search },
  'orka.switchboard': { label: 'Switchboard',        Icon: ListChecks },
  'orka.finance':     { label: 'Finance',            Icon: ListChecks },
  'orka.recruitment': { label: 'Recruitment',        Icon: ListChecks },
};

const STAGE_LABEL_FALLBACK = {
  thinking:     'Reading your message',
  choosing:     'Choosing the right tools',
  calling:      'Calling tools',
  synthesizing: 'Putting your answer together',
  done:         'Done',
};

function describeCapability(capabilityId, capabilityName) {
  if (!capabilityId) return capabilityName || 'A tool';
  const parts = capabilityId.split('.');
  const domainKey = parts.slice(0, 2).join('.');
  const verb = (parts[parts.length - 1] || '').replace(/_/g, ' ');
  const meta = DOMAIN_LABEL[domainKey];
  if (meta) return `${meta.label} — ${verb}`;
  return capabilityName || capabilityId;
}

function iconForCapability(capabilityId) {
  if (!capabilityId) return Wrench;
  const domainKey = capabilityId.split('.').slice(0, 2).join('.');
  return DOMAIN_LABEL[domainKey]?.Icon || Wrench;
}

// Status meta — uses semantic token classes (orka-text-*) instead of raw colours.
const STATUS_META = {
  completed:            { Icon: CheckCircle2,  textClass: 'orka-text-success', label: 'done' },
  pending_confirmation: { Icon: Clock,         textClass: 'orka-text-warning', label: 'awaiting confirmation' },
  failed:               { Icon: AlertTriangle, textClass: 'orka-text-danger',  label: 'failed' },
  running:              { Icon: Loader2,       textClass: 'orka-text-info',    label: 'running…', spin: true },
  pending:              { Icon: Clock,         textClass: 'orka-text-tertiary', label: 'queued' },
};


export const ThinkingPanel = ({
  isLive = false,
  scratchpad = [],
  liveRunId = null,
  liveStopWhenDone = true,
  defaultOpen = false,
}) => {
  const [liveSnapshot, setLiveSnapshot] = useState(null);

  useEffect(() => {
    if (!isLive || !liveRunId) return;
    let cancelled = false;
    let consecutiveErrors = 0;
    const poll = async () => {
      while (!cancelled) {
        try {
          const r = await fetch(`/api/ai/chat/live/${encodeURIComponent(liveRunId)}`, {
            credentials: 'include',
          });
          if (!r.ok) {
            consecutiveErrors += 1;
            if (consecutiveErrors > 3) break;
          } else {
            const snap = await r.json();
            if (cancelled) break;
            setLiveSnapshot(snap);
            if (liveStopWhenDone && snap.finished) break;
            consecutiveErrors = 0;
          }
        } catch (e) {
          consecutiveErrors += 1;
          if (consecutiveErrors > 3) break;
        }
        await new Promise(res => setTimeout(res, 400));
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [isLive, liveRunId, liveStopWhenDone]);

  const steps = useMemo(() => {
    if (isLive) {
      const realSteps = (liveSnapshot && liveSnapshot.steps) ? liveSnapshot.steps : [];
      if (realSteps.length > 0) {
        return realSteps.map((s, i) => ({
          step: s.step ?? i + 1,
          capability_id: s.capability_id,
          capability_name: s.capability_name || describeCapability(s.capability_id),
          status: s.status,
          summary: s.summary,
          Icon: iconForCapability(s.capability_id),
        }));
      }
      const stageLabel = liveSnapshot?.stage_label || 'Reading your message and deciding what to do';
      return [{
        step: 1,
        capability_id: null,
        capability_name: stageLabel,
        status: 'running',
        Icon: Brain,
      }];
    }
    return (scratchpad || []).map((s, i) => ({
      step: s.step ?? i + 1,
      capability_id: s.capability_id,
      capability_name: s.capability_name || describeCapability(s.capability_id),
      status: s.status,
      summary: s.summary,
      action_run_id: s.action_run_id,
      undo_deadline: s.undo_deadline,
      Icon: iconForCapability(s.capability_id),
    }));
  }, [isLive, scratchpad, liveSnapshot]);

  if (!isLive && steps.length === 0) return null;

  const totalDone = steps.filter(s => s.status === 'completed').length;
  const totalFailed = steps.filter(s => s.status === 'failed').length;
  const inFlight = steps.some(s => s.status === 'running' || s.status === 'pending');
  const liveStageLabel = liveSnapshot?.stage_label || STAGE_LABEL_FALLBACK[liveSnapshot?.stage || 'thinking'];
  const summaryLabel = isLive
    ? (liveStageLabel || 'Working…')
    : `${totalDone} step${totalDone !== 1 ? 's' : ''} done${totalFailed ? ` · ${totalFailed} failed` : ''}`;

  // Map state to the canonical agent-tone grammar (Code Reference §3.5)
  const tone = totalFailed
    ? 'failure'
    : inFlight
      ? (steps.length <= 1 && isLive ? 'thinking' : 'running')
      : 'done';

  const title = isLive ? 'Orka is working…' : 'How I built this answer';
  const stepSummary = `${steps.length} step${steps.length !== 1 ? 's' : ''} · ${summaryLabel}`;

  return (
    <OrkaDisclosure
      tone={tone}
      title={title}
      summary={stepSummary}
      defaultOpen={defaultOpen}
      testId="thinking-panel"
    >
      <div className="space-y-1.5" data-testid="thinking-panel-body">
        {steps.map((s, i) => {
          const meta = STATUS_META[s.status] || STATUS_META.pending;
          const Status = meta.Icon;
          const Icon = s.Icon || Wrench;
          return (
            <div key={`${s.step}-${i}`}
              className="flex items-start gap-2 text-[11px]"
              data-testid={`thinking-step-${s.step}`}>
              <span className="text-[10px] font-mono orka-text-tertiary w-4 flex-shrink-0 pt-0.5">{s.step}</span>
              <Icon className="w-3.5 h-3.5 orka-text-tertiary flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className={cn('font-medium truncate', meta.textClass)}>
                    {s.capability_name || describeCapability(s.capability_id)}
                  </span>
                  <span className="text-[9px] uppercase tracking-wider orka-text-tertiary">{meta.label}</span>
                </div>
                {s.summary && (
                  <div className="text-[10px] orka-text-secondary mt-0.5 line-clamp-2">
                    {s.summary}
                  </div>
                )}
                {s.capability_id && (
                  <div className="text-[9px] font-mono orka-text-tertiary mt-0.5 truncate">
                    {s.capability_id}
                  </div>
                )}
              </div>
              <Status
                className={cn('w-3 h-3 flex-shrink-0 mt-1', meta.textClass, meta.spin && 'animate-spin')}
                aria-hidden="true"
              />
            </div>
          );
        })}
      </div>
    </OrkaDisclosure>
  );
};

export default ThinkingPanel;
